/**
 * Omnipresent AI Store
 *
 * Central state management for the Omnipresent AI Chat Layer.
 * Manages chat modes, context awareness, and AI interactions.
 *
 * Architecture:
 * - Uses Zustand for lightweight, performant state management
 * - Integrates with TanStack Query for server state
 * - Supports 3 adaptive chat modes (hidden/sidebar/fullscreen)
 * - Handles context capture and AI context awareness
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { useShallow } from 'zustand/react/shallow';

// Chat display modes
export type ChatMode = 'hidden' | 'sidebar' | 'fullscreen';

// Chat interaction modes (conversational vs document)
export type InteractionMode = 'conversational' | 'document';

// Context types the AI can observe
export type ContextType =
  | 'lead-table'
  | 'lead-detail'
  | 'campaign-view'
  | 'dashboard'
  | 'settings'
  | 'unknown';

// Message types in conversation
export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: number;
  error?: boolean; // Failed to send
  context?: {
    type: ContextType;
    data?: Record<string, unknown>;
  };
  metadata?: {
    tokens?: number;
    model?: string;
    latency?: number;
  };
}

// Conversation thread
export interface Conversation {
  id: string;
  title: string; // Auto-generated from first message or user-edited
  messages: ChatMessage[];
  createdAt: number;
  updatedAt: number;
  preview?: string; // First user message preview
}

// Captured context from current view
export interface CapturedContext {
  type: ContextType;
  timestamp: number;
  data: Record<string, unknown>;
  viewportSnapshot?: string;
}

// AI Store State
export interface OmnipresentAIState {
  // Display state
  mode: ChatMode;
  interactionMode: InteractionMode; // Conversational vs Document mode
  isTransitioning: boolean;
  previousMode: ChatMode | null;
  sidebarWidth: number; // Width in pixels for sidebar mode

  // Conversation state
  sessionId: string;
  messages: ChatMessage[];
  isTyping: boolean;
  inputValue: string;

  // Conversation history
  conversations: Conversation[];
  currentConversationId: string | null;

  // Context awareness
  currentContext: CapturedContext | null;
  contextHistory: CapturedContext[];
  isContextCapturing: boolean;

  // AI state
  isProcessing: boolean;
  error: string | null;

  // User preferences (persisted)
  preferences: {
    defaultMode: ChatMode;
    enableContextCapture: boolean;
    enableMouseFollow: boolean;
    soundEnabled: boolean;
    autoExpandOnMention: boolean;
  };
}

// AI Store Actions
export interface OmnipresentAIActions {
  // Mode management
  setMode: (mode: ChatMode) => void;
  toggleMode: () => void;
  expandToSidebar: () => void;
  expandToFullscreen: () => void;
  minimize: () => void;
  setSidebarWidth: (width: number) => void;
  setInteractionMode: (mode: InteractionMode) => void;
  toggleInteractionMode: () => void;

  // Message management
  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => void;
  updateLastMessage: (content: string) => void;
  deleteMessage: (messageId: string) => void;
  clearMessages: () => void;
  setInputValue: (value: string) => void;
  setIsTyping: (isTyping: boolean) => void;
  regenerateSession: () => void;

  // Conversation history management
  createNewConversation: () => void;
  loadConversation: (conversationId: string) => void;
  deleteConversation: (conversationId: string) => void;
  renameConversation: (conversationId: string, title: string) => void;
  saveCurrentConversation: () => void;

  // Context management
  captureContext: (context: Omit<CapturedContext, 'timestamp'>) => void;
  clearContext: () => void;
  setContextCapturing: (isCapturing: boolean) => void;

  // AI processing
  setProcessing: (isProcessing: boolean) => void;
  setError: (error: string | null) => void;

  // Preferences
  updatePreferences: (updates: Partial<OmnipresentAIState['preferences']>) => void;

  // Reset
  reset: () => void;
}

// Combined store type
export type OmnipresentAIStore = OmnipresentAIState & OmnipresentAIActions;

// Initial state
const initialState: OmnipresentAIState = {
  mode: 'hidden',
  interactionMode: 'conversational', // Default to conversational mode
  isTransitioning: false,
  previousMode: null,
  sidebarWidth: 550, // Default sidebar width
  sessionId: crypto.randomUUID(),
  messages: [],
  isTyping: false,
  inputValue: '',
  conversations: [],
  currentConversationId: null,
  currentContext: null,
  contextHistory: [],
  isContextCapturing: false,
  isProcessing: false,
  error: null,
  preferences: {
    defaultMode: 'hidden',
    enableContextCapture: true,
    enableMouseFollow: true,
    soundEnabled: false,
    autoExpandOnMention: true,
  },
};

// Generate unique ID for messages
const generateId = () => `msg_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

// Create the store with persistence and immer for immutable updates
export const useOmnipresentAIStore = create<OmnipresentAIStore>()(
  persist(
    immer((set, get) => ({
      ...initialState,

      // Mode management
      setMode: (mode) =>
        set((state) => {
          state.previousMode = state.mode;
          state.mode = mode;
          state.isTransitioning = true;
          // Reset transitioning after animation
          setTimeout(() => {
            set((s) => {
              s.isTransitioning = false;
            });
          }, 300);
        }),

      toggleMode: () => {
        const { mode } = get();
        const nextMode: ChatMode = mode === 'hidden' ? 'sidebar' : mode === 'sidebar' ? 'fullscreen' : 'hidden';
        get().setMode(nextMode);
      },

      expandToSidebar: () => get().setMode('sidebar'),

      expandToFullscreen: () => get().setMode('fullscreen'),

      minimize: () => get().setMode('hidden'),

      setSidebarWidth: (width) =>
        set((state) => {
          // Constrain width between 300px and 800px
          state.sidebarWidth = Math.max(300, Math.min(800, width));
        }),

      setInteractionMode: (mode) =>
        set((state) => {
          state.interactionMode = mode;
        }),

      toggleInteractionMode: () =>
        set((state) => {
          state.interactionMode = state.interactionMode === 'conversational' ? 'document' : 'conversational';
        }),

      // Message management
      addMessage: (message) =>
        set((state) => {
          const newMessage: ChatMessage = {
            ...message,
            id: generateId(),
            timestamp: Date.now(),
          };
          state.messages.push(newMessage);

          // Keep only last 100 messages to prevent memory issues
          if (state.messages.length > 100) {
            state.messages = state.messages.slice(-100);
          }
        }),

      updateLastMessage: (content) =>
        set((state) => {
          if (state.messages.length > 0) {
            const lastMessage = state.messages[state.messages.length - 1];
            lastMessage.content = content;
          }
        }),

      deleteMessage: (messageId) =>
        set((state) => {
          state.messages = state.messages.filter((msg) => msg.id !== messageId);
        }),

      clearMessages: () =>
        set((state) => {
          state.messages = [];
        }),

      setInputValue: (value) =>
        set((state) => {
          state.inputValue = value;
        }),

      setIsTyping: (isTyping) =>
        set((state) => {
          state.isTyping = isTyping;
        }),

      regenerateSession: () =>
        set((state) => {
          state.sessionId = crypto.randomUUID();
        }),

      // Conversation history management
      createNewConversation: () =>
        set((state) => {
          // Save current conversation if it has messages
          if (state.messages.length > 0 && state.currentConversationId) {
            const existingIndex = state.conversations.findIndex(
              (c) => c.id === state.currentConversationId
            );
            if (existingIndex >= 0) {
              state.conversations[existingIndex].messages = [...state.messages];
              state.conversations[existingIndex].updatedAt = Date.now();
            }
          } else if (state.messages.length > 0) {
            // Save as new conversation first
            const firstUserMessage = state.messages.find((m) => m.role === 'user');
            const newConversation: Conversation = {
              id: crypto.randomUUID(),
              title: firstUserMessage?.content.slice(0, 50) || 'New Chat',
              messages: [...state.messages],
              createdAt: Date.now(),
              updatedAt: Date.now(),
              preview: firstUserMessage?.content.slice(0, 100),
            };
            state.conversations.unshift(newConversation);
          }

          // Create fresh conversation
          state.messages = [];
          state.currentConversationId = null;
          state.sessionId = crypto.randomUUID();
          state.inputValue = '';
        }),

      loadConversation: (conversationId) =>
        set((state) => {
          // Save current conversation first if it has messages
          if (state.messages.length > 0 && state.currentConversationId) {
            const existingIndex = state.conversations.findIndex(
              (c) => c.id === state.currentConversationId
            );
            if (existingIndex >= 0) {
              state.conversations[existingIndex].messages = [...state.messages];
              state.conversations[existingIndex].updatedAt = Date.now();
            }
          } else if (state.messages.length > 0) {
            // Save unsaved conversation
            const firstUserMessage = state.messages.find((m) => m.role === 'user');
            const newConversation: Conversation = {
              id: crypto.randomUUID(),
              title: firstUserMessage?.content.slice(0, 50) || 'New Chat',
              messages: [...state.messages],
              createdAt: Date.now(),
              updatedAt: Date.now(),
              preview: firstUserMessage?.content.slice(0, 100),
            };
            state.conversations.unshift(newConversation);
          }

          // Load the selected conversation
          const conversation = state.conversations.find((c) => c.id === conversationId);
          if (conversation) {
            state.messages = [...conversation.messages];
            state.currentConversationId = conversationId;
            state.sessionId = conversationId; // Use conversation ID as session ID for continuity
          }
        }),

      deleteConversation: (conversationId) =>
        set((state) => {
          state.conversations = state.conversations.filter((c) => c.id !== conversationId);
          // If deleting current conversation, clear messages
          if (state.currentConversationId === conversationId) {
            state.messages = [];
            state.currentConversationId = null;
            state.sessionId = crypto.randomUUID();
          }
        }),

      renameConversation: (conversationId, title) =>
        set((state) => {
          const conversation = state.conversations.find((c) => c.id === conversationId);
          if (conversation) {
            conversation.title = title;
            conversation.updatedAt = Date.now();
          }
        }),

      saveCurrentConversation: () =>
        set((state) => {
          if (state.messages.length === 0) return;

          const firstUserMessage = state.messages.find((m) => m.role === 'user');

          if (state.currentConversationId) {
            // Update existing conversation
            const existingIndex = state.conversations.findIndex(
              (c) => c.id === state.currentConversationId
            );
            if (existingIndex >= 0) {
              state.conversations[existingIndex].messages = [...state.messages];
              state.conversations[existingIndex].updatedAt = Date.now();
              if (firstUserMessage && !state.conversations[existingIndex].preview) {
                state.conversations[existingIndex].preview = firstUserMessage.content.slice(0, 100);
              }
            }
          } else {
            // Create new conversation
            const newId = crypto.randomUUID();
            const newConversation: Conversation = {
              id: newId,
              title: firstUserMessage?.content.slice(0, 50) || 'New Chat',
              messages: [...state.messages],
              createdAt: Date.now(),
              updatedAt: Date.now(),
              preview: firstUserMessage?.content.slice(0, 100),
            };
            state.conversations.unshift(newConversation);
            state.currentConversationId = newId;

            // Keep only last 50 conversations
            if (state.conversations.length > 50) {
              state.conversations = state.conversations.slice(0, 50);
            }
          }
        }),

      // Context management
      captureContext: (context) =>
        set((state) => {
          const newContext: CapturedContext = {
            ...context,
            timestamp: Date.now(),
          };
          state.currentContext = newContext;

          // Keep context history (last 20 entries)
          state.contextHistory.push(newContext);
          if (state.contextHistory.length > 20) {
            state.contextHistory = state.contextHistory.slice(-20);
          }
        }),

      clearContext: () =>
        set((state) => {
          state.currentContext = null;
        }),

      setContextCapturing: (isCapturing) =>
        set((state) => {
          state.isContextCapturing = isCapturing;
        }),

      // AI processing
      setProcessing: (isProcessing) =>
        set((state) => {
          state.isProcessing = isProcessing;
          if (isProcessing) {
            state.error = null;
          }
        }),

      setError: (error) =>
        set((state) => {
          state.error = error;
          state.isProcessing = false;
        }),

      // Preferences
      updatePreferences: (updates) =>
        set((state) => {
          state.preferences = { ...state.preferences, ...updates };
        }),

      // Reset to initial state (preserves preferences)
      reset: () =>
        set((state) => {
          const { preferences } = state;
          Object.assign(state, { ...initialState, preferences });
        }),
    })),
    {
      name: 'omnipresent-ai-storage',
      storage: createJSONStorage(() => localStorage),
      // Persist preferences and conversation history
      partialize: (state) => ({
        preferences: state.preferences,
        mode: state.preferences.defaultMode,
        interactionMode: state.interactionMode,
        conversations: state.conversations,
      }),
    }
  )
);

// Selector hooks for optimized re-renders
export const useAIChatMode = () => useOmnipresentAIStore((state) => state.mode);
export const useAIInteractionMode = () => useOmnipresentAIStore((state) => state.interactionMode);
export const useAIMessages = () => useOmnipresentAIStore((state) => state.messages);
export const useAIContext = () => useOmnipresentAIStore((state) => state.currentContext);
export const useAIProcessing = () =>
  useOmnipresentAIStore(
    useShallow((state) => ({
      isProcessing: state.isProcessing,
      isTyping: state.isTyping,
      error: state.error,
    }))
  );
export const useAIPreferences = () => useOmnipresentAIStore((state) => state.preferences);
export const useAIConversations = () => useOmnipresentAIStore((state) => state.conversations);
export const useCurrentConversationId = () => useOmnipresentAIStore((state) => state.currentConversationId);

// Action hooks for cleaner component code
export const useAIActions = () =>
  useOmnipresentAIStore(
    useShallow((state) => ({
      setMode: state.setMode,
      toggleMode: state.toggleMode,
      expandToSidebar: state.expandToSidebar,
      expandToFullscreen: state.expandToFullscreen,
      minimize: state.minimize,
      setSidebarWidth: state.setSidebarWidth,
      setInteractionMode: state.setInteractionMode,
      toggleInteractionMode: state.toggleInteractionMode,
      addMessage: state.addMessage,
      updateLastMessage: state.updateLastMessage,
      deleteMessage: state.deleteMessage,
      clearMessages: state.clearMessages,
      setInputValue: state.setInputValue,
      regenerateSession: state.regenerateSession,
      captureContext: state.captureContext,
      clearContext: state.clearContext,
      setProcessing: state.setProcessing,
      setError: state.setError,
      reset: state.reset,
      createNewConversation: state.createNewConversation,
      loadConversation: state.loadConversation,
      deleteConversation: state.deleteConversation,
      renameConversation: state.renameConversation,
      saveCurrentConversation: state.saveCurrentConversation,
    }))
  );
